import { useState, useRef, useEffect } from 'react'
import { Header, InlineHeader, InlineSection, useIsReloading, Content, Pagination } from '@/components/DashboardComponents'
import { Button, Spacing, RoundIconButton, Dropdown, DropdownOption, Textbox, Checkbox, HalfSpacing, RadioGroup, RadioOption } from '@/form/FormComponents'
import { Outlet, useLoaderData, useNavigate, useRouteLoaderData, useLocation, useParams, useRevalidator } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { Icon } from '@/components/Icon'
import { useIsDeveloper, usePrivacyMode } from '@/systems/Authentication'
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal, useCopyPromptModal } from '@/components/Modal'
import { useMenu } from '@/components/Menu'
import { publicAction } from '@/data/loaders';
import { useRenameAssessmentModal, useDeleteAssessmentModal } from '@/pages/Assessment';
import { useRenameCourseModal, useArchiveCourseModal } from '@/pages/Courses';
import { FormProvider, FormSubmitProvider } from '@/form/FormProvider';
import { ShareBox } from '@/form/ShareBox';
import { markdown } from 'markdown'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useFilePickerModal } from '@/components/FilePickerModals'
import { List, ListItem, ItemColumn } from '@/components/List'

export function Course() {
  const course = useLoaderData().data.value;
  const location = useLocation();
  const assessment = location.pathname.includes('/assessments/');
  const { organization_id } = useParams();
  const is_mobile = useIsMobile();

  const [openRenameCourseModal] = useRenameCourseModal();
  const [openArchiveCourseModal] = useArchiveCourseModal();

  let tabs = [
    {to: (organization_id ? `/organizations/${organization_id}` : '')+`/courses/${course._id}`, name: "Assessments", end: true},
    {to: (organization_id ? `/organizations/${organization_id}` : '')+`/courses/${course._id}/people`, name: "People"}
  ];
  if (course.membership_type == "educator") {
    tabs.push({to: (organization_id ? `/organizations/${organization_id}` : '')+`/courses/${course._id}/settings`, name: "Settings"});
  }

  const menuItems = (course.membership_type == "educator" && !course.archived) ? [
    {
      label: 'Rename',
      icon: 'rename-outline',
      onClick: () => openRenameCourseModal({ course_id: course._id, name: course.name }),
    },
    "separator",
    {
      label: 'Archive',
      icon: 'archive',
      onClick: () => openArchiveCourseModal({ course_id: course._id }),
    }
  ] : undefined;

  if (assessment) {
    return <Outlet/>;
  }

  return (
    <>
      <Header title={course.name+(course.section_name ? ` (${course.section_name})` : "")} tabs={tabs.length > 1 ? tabs : false} backButton={course.archived ? "/archived-courses" : (organization_id ? `/organizations/${organization_id}` : (is_mobile ? "/courses" : false))} menuItems={menuItems}>
        {/* {course.membership_type == "educator" ? <Button className="primary" icon="plus" text="Assign scenario" onClick={() => navigate(`/courses/${course._id}/new`)}/> : null} */}
      </Header>
      <Outlet/>
    </>
  )
}

export function Assessments() {
  const data = useLoaderData().data.value;
  const course = useRouteLoaderData("course").data.value;

  const assessments = data.assessments;
  const membership_type = data.membership_type;

  const [openAssessmentModal, setOpenAssessmentModal] = useState(null);

  const handleOpenAssessment = (id) => {
    if (openAssessmentModal == id) {
      setOpenAssessmentModal(null);
    } else {
      setOpenAssessmentModal(id);

    }
  }

  const navigate = useNavigate();

  const archived_banner = (course.archived ? (
    <div className="callout_hint_box">
      <div>
        <div><Icon name="archive"/></div>
        <div>{`This course is archived.`}</div>
      </div>
    </div>
  ) : null)

  const [openFilePickerModal] = useFilePickerModal();

  const handleNewAssessment = () => {
    openFilePickerModal({
      mode: "choose",
      type: "scenario",
      callback: ({ file, closeModal }) => {
        closeModal();
        navigate("/courses/"+course._id+"/assessments/new/"+file.item_id);
      }
    })
  }

  return (
    <div className="dashboard_content_inner nearest_scrollable_container">
      <div className="assessments_section">
        {archived_banner}
        {membership_type == "educator" && !course.archived && <>
          <InlineHeader>
            <Button icon="assignment-plus" text="Assign a scenario" onClick={handleNewAssessment}/>
          </InlineHeader>
          <HalfSpacing/>
        </>}
        {assessments.length > 0 ? <div className="scenario_list" columns="2">
          {assessments.sort((a, b) => a.date_created - b.date_created).map((assessment) => <AssessmentItem key={assessment._id} assessment={assessment} membership_type={membership_type} course_id={course._id} is_open={openAssessmentModal == assessment._id} handleOpenAssessment={handleOpenAssessment} />)}
        </div> : <GenericEmptyListPlaceholder text="No scenarios have been assigned yet"/>}
      </div>
    </div>
  )
}

function AssessmentItem({assessment, membership_type, course_id, is_open, handleOpenAssessment}) {
  const navigate = useNavigate();

  const [openDownloadModal] = useCopyPromptModal({ title: assessment.data.title, text: assessment._dev_details_prompt, filename: assessment.data.title });
  const is_dev = useIsDeveloper();
  const [openRenameAssessmentModal] = useRenameAssessmentModal();
  const [openDeleteAssessmentModal] = useDeleteAssessmentModal();

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      {
        label: 'Rename',
        icon: 'rename-outline',
        onClick: () => openRenameAssessmentModal({ assessment_id: assessment._id, course_id: assessment.course_id, title: assessment.data.title })
      },
      "separator",
      // {
      //   label: 'Edit',
      //   icon: 'pencil-outline',
      //   onClick: () => navigate("/courses/"+course_id+"/assessments/"+assessment._id+"/edit"),
      // },
      // {
      //   label: 'Preview',
      //   icon: 'play-outline',
      //   onClick: () => navigate("/courses/"+course_id+"/assessments/"+assessment._id+"/preview"),
      // },
      // "separator",
      // {
      //   label: 'Review submissions',
      //   icon: 'clipboard-check-outline',
      //   onClick: () => navigate("/courses/"+course_id+"/assessments/"+assessment._id+"/grader"),
      // },
      // "separator",
      {
        label: 'Delete',
        icon: 'trash-can-outline',
        onClick: () => openDeleteAssessmentModal({ assessment_id: assessment._id, course_id: assessment.course_id }),
      },
      ...(is_dev ? [
        "separator",
        {
          label: 'Download as text',
          icon: 'file-download-outline',
          onClick: openDownloadModal,
        }
      ] : [])
    ]
  });

  let due_date, color = "a";
  if(membership_type == 'student') {
    if(assessment.session) {
      // Assessment has been started
      if(assessment.session.submitted) {
        if(assessment.session.returned) {
          if(assessment.data.due_date && assessment.session.submitted_date > assessment.data.due_date) {
            due_date = 'Returned (late)';
          } else {
            due_date = 'Returned';
          }
        } else {
          if(assessment.data.due_date && assessment.session.submitted_date > assessment.data.due_date) {
            due_date = 'Submitted (late)';
          } else {
            due_date = 'Submitted';
          }
        }
      } else {
        due_date = 'In progress';
        color = 'green';
      }
    } else if(assessment.data.due_date) {
      const date = new Date(assessment.data.due_date);
      const today = new Date();

      const options = { month: 'short', day: 'numeric' };
      if (date.getFullYear() !== today.getFullYear()) {
        options.year = 'numeric';
      }
      if (date.getHours() !== 23 || date.getMinutes() !== 59) {
        options.hour = 'numeric';
        options.minute = 'numeric';
      }
      const date_string = new Intl.DateTimeFormat('en-US', options).format(date);
  
      if (date < today) {
        if(assessment.data.allow_late_submissions) {
          due_date = `Overdue ${date_string}`;
          color = 'red';
        } else {
          due_date = `Due ${date_string}`;
        }
      } else {
        due_date = `Due ${date_string}`;
      }
    } else {
      due_date = 'No due date';
    }
  } else {
    // Educator
    if(assessment.data.due_date) {
      const date = new Date(assessment.data.due_date);
      const today = new Date();

      const options = { month: 'short', day: 'numeric' };
      if (date.getFullYear() !== today.getFullYear()) {
        options.year = 'numeric';
      }
      if (date.getHours() !== 23 || date.getMinutes() !== 59) {
        options.hour = 'numeric';
        options.minute = 'numeric';
      }
      due_date = `Due ${new Intl.DateTimeFormat('en-US', options).format(date)}`;
    } else {
      const date = new Date(assessment.date_created);
      const today = new Date();

      const options = { month: 'short', day: 'numeric' };
      if (date.getFullYear() !== today.getFullYear()) {
        options.year = 'numeric';
      }

      due_date = `Posted ${new Intl.DateTimeFormat('en-US', options).format(date)}`;
    }
  }

  return (
    <div className="assessment_item">
      <div className="scenario_item" onClick={() => membership_type == "educator" ? navigate("/courses/"+course_id+"/assessments/"+assessment._id) : handleOpenAssessment(assessment._id)} onContextMenu={membership_type == "educator" ? handleContextMenu : null} tabIndex={0}>
        <div className="item_icon">
          <Icon name="assignment"/>
        </div>
        <div className="item_title">{assessment.data.title}</div>
        <div className="item_date" style={{color: `var(--${color}text)`}}>{due_date}</div>
        {
          membership_type == "educator" ? (
            <div className="item_more">
              <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
            </div>
          ) : (
            null
          )
        }
      </div>
      {is_open ? <AssessmentExpanded assessment={assessment}/> : null}
    </div>
  )
}

function AssessmentExpanded({assessment}) {
  const navigate = useNavigate();
  const course = useRouteLoaderData("course").data.value;
  
  let date_string = "No due date";
  if (assessment.data.due_date) {
    const date = new Date(assessment.data.due_date);
    const today = new Date();

    const options = { month: 'short', day: 'numeric' };
    if (date.getFullYear() !== today.getFullYear()) {
      options.year = 'numeric';
    }
    if (date.getHours() !== 23 || date.getMinutes() !== 59) {
      options.hour = 'numeric';
      options.minute = 'numeric';
    }

    date_string = "Due " + new Intl.DateTimeFormat('en-US', options).format(date);
  }

  return (
    <div className="assessment_details">
      <div className="assessment_details_description">
        {course.membership_type == "educator" ? (
          <div className="assessment_completion floating" onClick={() => {navigate("/courses/"+course._id+"/assessments/"+assessment._id+"/submissions")}}>
            <div>
              <div>{assessment.completion_counts.assigned || 0}</div>
              <div>Assigned</div>
            </div>
            <div>
              <div>{assessment.completion_counts.submitted || 0}</div>
              <div>Completed</div>
            </div>
            <div>
              <div>{assessment.completion_counts.returned || 0}</div>
              <div>Returned</div>
            </div>
          </div>
        ) : null}
        <div className="assessment_due_date selectable">{date_string}</div>
        {assessment.data.description ? <div className="assessment_description selectable markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdown.toHTML(assessment.data.description)}}></div> : null}
      </div>
      <div className="assessment_primary_action">
        {course.membership_type == "educator" ? 
          <>
            <Button text="View details" className="hollow" onClick={() => {navigate("/courses/"+course._id+"/assessments/"+assessment._id)}} />
            <Button className="primary" text="Review submissions" onClick={() => navigate("/courses/"+course._id+"/assessments/"+assessment._id+"/submissions")}/>
          </>
        : 
          (assessment.session?.submitted ? 
            (assessment.session?.returned ?
              <Button icon="comment-text-outline" text="View feedback" onClick={() => navigate("/courses/"+course._id+"/assessments/"+assessment._id+"/review")} /> 
            :
              <Button icon="clipboard-text-search-outline" text="Review assessment" onClick={() => navigate("/courses/"+course._id+"/assessments/"+assessment._id+"/review")} /> 
            )
          : 
            <Button icon="play" text={assessment.session ? "Resume assessment" : "Start assessment"} className="primary" disabled={course.archived} onClick={() => {navigate(assessment.session ? "/session/"+assessment.session._id : "/courses/"+course._id+"/assessments/"+assessment._id+"/start")}} />
          )
        }
      </div>
    </div>
  )
}

export const useShouldShareFilesWithCoInstructorsModal = () => {

  const [openShouldShareFilesModal] = useModal(({ context, closeModal, setLoading }) => {
    const { course_id, callback, share_prompt_files, from_course_enrollment = false } = context;

    function submitForm({ formatted_data, focusError }) {
      setLoading(true);

      publicAction("updateCourse", {
        course_id: course_id,
        data: {
          _should_share_files: formatted_data._should_share_files
        }
      }).then(function() {

        if (from_course_enrollment && formatted_data._should_share_files === "yes") {
          publicAction("shareFiles", {
            ids: share_prompt_files.map(file => file.file_id),
            people: [{ course_id }],
            level: "edit",
            notify_people: false,
            message: null,
          }).then(function() {
            closeModal();
            callback?.({ should_share_files: formatted_data._should_share_files });
          }).catch(function(error) {
            console.error(error);
            setLoading(false);
            focusError({name: "_should_share_files", error: "Something went wrong, please try again."});
          });
        } else {
          closeModal();
          callback?.({ should_share_files: formatted_data._should_share_files });
        }

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "_should_share_files", error: "Something went wrong, please try again."});
      })
    }

    function handleAskMeLater() {
      closeModal();
      callback?.({});
    }

    return (
      <Modal title={`Allow co-instructors to edit files?`} width={380}>
        <FormProvider onSubmit={submitForm}>
          <ModalContent>
            <span>{`Do you want to automatically share `}<b style={{fontWeight: "500"}}>{`edit`}</b>{` access with your co-instructors for all the files you use in this course?`}</span>
            <Spacing/>
            {/* {share_prompt_files ? ( */}
              <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "10px"}}>
                {share_prompt_files.slice(0, 3).map(file => (
                  <div className="share_file_pill" key={file.file_id}>{file.type === "scenario" ? <Icon name="forum-outline" style={{fill: "var(--scenario_file_color)"}}/> : <Icon name="bullseye-arrow" style={{fill: "var(--rubric_file_color)"}}/>}<span>{file.title}</span></div>
                ))}
              </div>
            {/* ) : (
              <span>{`For example, if you create a new scenario or rubric and then assign it to this course, it'll automatically be shared with your co-instructors so they can edit it too.`}</span>
            )} */}
            <Spacing/>
            <RadioGroup name="_should_share_files" required="Please choose an option">
              <RadioOption name="yes" text={"Yes, always automatically share edit access"} />
              <RadioOption name="no" text="No, never share edit access" />
              <RadioOption name="ask" text="Let me decide for each file" />
            </RadioGroup>
          </ModalContent>
          <ModalButtons>
            <Button text="Ask me later" onClick={handleAskMeLater} />
            <FormSubmitProvider>
              {({ handleSubmit }) => (
                <Button text="Submit" className="primary" onClick={handleSubmit} />
              )}
            </FormSubmitProvider>
          </ModalButtons>
        </FormProvider>
      </Modal>
    )
  })

  return [openShouldShareFilesModal];
}

export function People() {
  const revalidator = useRevalidator();
  const people = useLoaderData().data.value;
  const course = useRouteLoaderData("course").data.value;
  const is_educator = course.membership_type == "educator";

  const students = people.members.filter(a => a.role == "student").sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const educators = people.members.filter(a => a.role == "educator").sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const [selected_section, setSelectedSection] = useState("all");
  const section_students = students.filter(a => selected_section == "all" || a.course_section_id == selected_section);

  const [openShouldShareFilesModal] = useShouldShareFilesWithCoInstructorsModal();

  const [openAddCourseMemberModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {
      setLoading(true);

      publicAction("addCourseMembers", {
        course_id: course._id,
        course_section_id: formatted_data.course_section_id || course.sections[0]._id,
        people: formatted_data.people,
        role: context.role
      }).then(function({ data }) {

        closeModal();
        revalidator.revalidate();

        if (context.role == "educator" && data.share_prompt_files?.length > 0 /*&& !course._should_share_files*/) {
          openShouldShareFilesModal({ course_id: course._id, share_prompt_files: data.share_prompt_files, from_course_enrollment: true });
        }

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "people", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title={`Add ${context.role == "educator" ? "instructors" : "learners"}`}>
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit, has_changed}) => (
              <>
                <ModalContent>
                  <ShareBox name="people"/>
                  {(context.role == "student" && course.sections.length > 1) ? <>
                    <Spacing/>
                    <Dropdown title="Section" name="course_section_id" required="Please choose a course section">
                      {course.sections.map(function(section, index) {
                        return <DropdownOption key={section._id} name={section._id} text={section.name || "Primary section"}/>
                      })}
                    </Dropdown>
                  </> : null}
                  <Spacing/>
                  {"We'll send them an email with a link to join the course."}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text={`Add ${context.role == "educator" ? "instructors" : "learners"}`} className="primary" disabled={!has_changed} onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openErrorModal] = useErrorModal();

  const [openInviteModal] = useModal(({ closeModal, setLoading }) => {
    const resetCode = function(section_id) {
      setLoading(true);
      publicAction("regenerateJoinCode", {
        id: course._id,
        section_id: section_id
      }).then(function() {
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Invite learners">
        <ModalContent>
          {course.sections.map(function(section, index) {
            let join_details = <>
              <label>Invite link</label>
              <CopyTextItem text={"https://sapientclinician.com/"+(course.study ? "study/" : "join/")+section.join_code}/>
              {course.study ? null : <>
                <Spacing/>
                <label>Course code</label>
                <CopyTextItem text={section.join_code}/>
              </>}
            </>

            if (course.sections.length > 1) {
              return <div key={index}>
                <InlineHeader title={section.name || "Primary section"} small={true}>
                  <Button icon="refresh" text="Reset code" className="small hollow" onClick={() => {
                    resetCode(section._id);
                  }}/>
                </InlineHeader>
                <InlineSection>
                  {join_details}
                </InlineSection>
              </div>
            } else {
              return <div key={index}>
                {join_details}
              </div>
            }
          })}
        </ModalContent>
        <ModalButtons space_between={course.sections.length == 1}>
          {course.sections.length == 1 ? <Button icon="refresh" text="Reset code" className="hollow" onClick={() => { resetCode(course.sections[0]._id); }}/> : null}
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, [course], true)
  
  return (
    <Content>
      <div className="small_width_section">
        <InlineHeader title="Instructors">
          {is_educator && <Button icon="account-plus" text="Add instructors" onClick={() => {openAddCourseMemberModal({role: "educator"})}}/>}
        </InlineHeader>
        <InlineSection>
          {educators.length > 0 ? (
            educators.map((person) => <PersonItem key={person._id} person={person} readOnly={!is_educator}/>)
          ) : <GenericEmptyListPlaceholder text="No instructors"/>}
        </InlineSection>
        {is_educator && <>
          <InlineHeader title={`Learners (${students.length})`}>
            <Button icon="link" text="Get invite link" onClick={openInviteModal}/>
            {course.study ? null : <Button icon="account-plus" text="Add learners" onClick={() => {openAddCourseMemberModal({role: "student"})}}/>}
          </InlineHeader>
          <InlineSection>
            {course.sections.length > 1 ? <>
              <Dropdown value={selected_section} onChange={(value) => {setSelectedSection(value)}} required="Please choose a course section">
                {["all"].concat(course.sections).map(function(section, index) {
                  if (index == 0) {
                    return <DropdownOption key="all" name="all" text={"All sections ("+students.length+")"}/>
                  }
                  return <DropdownOption key={section._id} name={section._id} text={(section.name || "Primary section")+" ("+students.filter(a => a.course_section_id == section._id).length+")"}/>
                })}
              </Dropdown>
              <HalfSpacing/>
            </> : null}
            {section_students.length > 0 ? (
              section_students.map((person) => <PersonItem key={person._id} person={person}/>)
            ) : <GenericEmptyListPlaceholder text={"No learners"+(course.sections.length > 1 ? " in this section" : "")}/>}
          </InlineSection>
        </>}
      </div>
    </Content>
  )
}

function PersonItem({ person, readOnly }) {
  const revalidator = useRevalidator();
  const course = useRouteLoaderData("course").data.value;
  const [openErrorModal] = useErrorModal();
  const privacyMode = usePrivacyMode();

  const [openRemovePersonModal] = useModal(({ closeModal, setLoading }) => {
    function handleSubmit() {
      setLoading(true);

      publicAction("removeCourseMember", {
        course_id: course._id,
        id: person._id
      }).then(function() {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })
    }

    return (
      <Modal title={`Remove this ${person.role == "educator" ? "instructor" : "learner"}?`}>
        <ModalContent>
          {person.role == "educator" ? "They'll completely lose access to the course." : "You won't be able to view their submissions anymore. The learner won't be notified."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Remove" className="destructive" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      {
        label: "Remove",
        icon: "close",
        onClick: () => {openRemovePersonModal()}
      }
    ]
  });

  return (
    <div className="person_item" onContextMenu={readOnly ? undefined : handleContextMenu}>
      <div className="person_item_icon">
        <Icon name={person._id.charAt(0) != "_" ? "account-circle-outline" : "email-outline"}/>
      </div>
      <div className="person_item_text">
        {person._id.charAt(0) == "_" ? <div>{person.email}</div> : <div>{(person.role == "student" && privacyMode) ? "[Learner name]" : (person.name || "(No name)")}</div>}
        {person._id.charAt(0) == "_" ? <div>Pending invitation</div> : (person.role == "educator" ? <div>{person.email}</div> : null)}
      </div>
      {readOnly ? null : (
        <div className="person_item_more">
          <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
        </div>
      )}
    </div>
  )
}

function CopyTextItem({text}) {
  const [justCopied, setJustCopied] = useState(false);
  const resetCopiedTimeout = useRef(null);

  const copyText = function() {
    navigator.clipboard.writeText(text);
    setJustCopied(true);

    if (resetCopiedTimeout.current) {
      clearTimeout(resetCopiedTimeout.current);
    }

    resetCopiedTimeout.current = setTimeout(() => {
      setJustCopied(false);
    }, 1500);
  }

  return (
    <div className="copy_text_item">
      <div className="text">{text}</div>
      <Button icon={justCopied ? "check" : "copy"} onClick={copyText}/>
    </div>
  )
}

const useOpenAddSectionModal = () => {
  const revalidator = useRevalidator();
  const course = useRouteLoaderData("course_settings").data.value;

  const [openAddSectionModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      setLoading(true);
      
      publicAction(context?.section ? "updateCourseSection" : "createCourseSection", {
        id: context?.section?._id,
        course_id: course._id,
        name: formatted_data.name,
      }).then(function() {

        closeModal();
        revalidator.revalidate();

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong."});
      })
    }

    const initial_data = context?.section ? {
      name: context.section.name,
    } : undefined;

    return (
      <Modal title={context?.section ? "Rename section" : "Add section"}>
        <FormProvider onSubmit={submitForm} initial_data={initial_data}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" title="Section name" required={true} onEnter={handleSubmit} />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return { openAddSectionModal };
}

export function CourseSettings() {
  const revalidator = useRevalidator();
  const course = useLoaderData().data.value;
  const initial_data = course;
  const [submitting, setIsSubmitting] = useState(false);
  const is_reloading = useIsReloading();
  const [openErrorModal] = useErrorModal();

  const courses = useRouteLoaderData("dashboard_root").data.value;
  const i_am_in_course = courses.some(c => c._id == course._id);

  function submitForm({ formatted_data }) {
    setIsSubmitting(true);

    publicAction("updateCourse", {
      course_id: course._id,
      data: formatted_data
    }).then(function(data) {
      setIsSubmitting(false);
      revalidator.revalidate();
    }).catch(function(error) {
      console.error(error);
      setIsSubmitting(false);
      openErrorModal();
    })
  }

  const { openAddSectionModal } = useOpenAddSectionModal();

  return (
    <FormProvider onSubmit={submitForm} initial_data={initial_data} unsaved_modal="organization_settings">
      <Content>
        <div className="small_width_section">
          <InlineHeader title="Course details"/>
          <InlineSection>
            <Textbox name="name" title="Course name" autofocus={true} required={true}/>
            <Spacing/>
            <RadioGroup title="Feedback mode" name="feedback_mode" default_value="review">
              <RadioOption name="review" text="Educator must review and return"/>
              <RadioOption name="auto-return" text="Return feedback immediately, without review"/>
            </RadioGroup>
            {/* <Spacing/> */}
          </InlineSection>
          <InlineHeader title="Sections">
            <Button icon="plus" text="Add section" onClick={() => {openAddSectionModal()}}/>
          </InlineHeader>
          <InlineSection>
            <List columns={[{ name: "Section name" }, { name: "Learners", width: 100 }]} menu={true}>
              {course.sections.map((section) => <SectionItem key={section._id} section={section} sections_count={course.sections.length}/>)}
            </List>
          </InlineSection>
          {(i_am_in_course && (course.multiple_instructors || course._should_share_files)) ? <>
            <InlineHeader title="Settings just for you"/>
            <InlineSection>
              <RadioGroup title={`Do you want to automatically share edit access with your co-instructors for all the files you use in this course? This will allow them to edit the original scenario and rubric files you use.`} hint="Note: Changing this option won't automatically share or unshare anything. It only applies going forward." name="_should_share_files">
                <RadioOption name="yes" text="Yes, always automatically share edit access"/>
                <RadioOption name="no" text="No, never share edit access"/>
                <RadioOption name="ask" text="Let me decide for each file"/>
              </RadioGroup>
            </InlineSection>
          </> : null}
        </div>
      </Content>
      <Pagination getContainerElement={() => document.querySelector("#organization_settings")}>
        <FormSubmitProvider>
          {({handleSubmit, has_changed}) => {
            return <Button className="primary" text="Save changes" onClick={handleSubmit} disabled={submitting || !has_changed || is_reloading} loading={submitting}/>
          }}
        </FormSubmitProvider>
      </Pagination>
    </FormProvider>
  )
}

function SectionItem({section, sections_count}) {
  const revalidator = useRevalidator();
  const course = useRouteLoaderData("course_settings").data.value;
  const { openAddSectionModal } = useOpenAddSectionModal();
  const [openErrorModal] = useErrorModal();

  const [openDeleteSectionModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      setLoading(true);
      
      publicAction("deleteCourseSection", {
        id: section._id,
        course_id: course._id,
        remediation: context.section._student_count > 0 ? (formatted_data.remediation == "remove" ? "remove" : "move") : "remove",
        remediation_section_id: formatted_data.remediation != "remove" ? formatted_data.remediation : null,
      }).then(function() {

        closeModal();
        revalidator.revalidate();

      }).catch(function(error) {
        console.error(error);
        if (context.section._student_count > 0) {
          setLoading(false);
          focusError({name: "remediation", error: "Something went wrong."});
        } else {
          closeModal();
          openErrorModal();
        }
      })
    }

    return (
      <Modal title={`Delete section ${context.section.name || "Primary section"}?`}>
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  You can't undo this.
                  {context.section._student_count > 0 ? <>
                    <Spacing/>
                    <Dropdown title="What should happen to the learners in this section?" name="remediation" required="Please choose an option">
                      {["remove"].concat(course.sections.filter(a => a._id != section._id)).map(function(section, index) {
                        if (index == 0) {
                          return <DropdownOption key="remove" name="remove" text="Remove them from the course"/>
                        }
                        return <DropdownOption key={section._id} name={section._id} text={"Move to "+(section.name || "Primary section")}/>
                      })}
                    </Dropdown>
                  </> : null}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Delete section" className="destructive" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const menu = useMenu({
    menuItems: [
      {
        label: "Rename",
        icon: "rename-outline",
        onClick: () => {openAddSectionModal({section})}
      },
      "separator",
      {
        label: 'Delete',
        icon: 'delete-forever-outline',
        disabled: sections_count == 1,
        onClick: () => {openDeleteSectionModal({section})}
      }
    ]
  });

  return (
    <ListItem icon="account-group-outline" menu={menu}>
      <ItemColumn>{section.name || "Primary section"}</ItemColumn>
      <ItemColumn>{section._student_count+" learner"+(section._student_count != 1 ? "s" : "")}</ItemColumn>
    </ListItem>
  )
}